import api from '../services/api';

export const ACTOR_CACHE_TTL_MS = 5 * 60 * 1000;
export const ACTOR_MISSING_CACHE_TTL_MS = 3 * 60 * 1000;

const actorCache = new Map();
const actorInFlight = new Map();
/** Index nom affiché → avatar_url (listes clients / intervenants / admins). */
const profileNameIndex = new Map();

export const normalizeActorRole = (value) => String(value || '').trim().toLowerCase();

export const normalizeProfileNameKey = (name) => String(name || '')
  .trim()
  .toLowerCase()
  .replace(/\s+/g, ' ');

export function profileNameCacheKey(role, name) {
  const r = normalizeActorRole(role);
  const n = normalizeProfileNameKey(name);
  if (!r || !n) return null;
  return `${r}::${n}`;
}

export function actorCacheKey(role, actorId) {
  return `${normalizeActorRole(role)}-${actorId}`;
}

export function peekActorFromCache(role, actorId) {
  if (actorId == null || actorId === '') return null;
  const cached = actorCache.get(actorCacheKey(role, actorId));
  if (!cached || cached.expiresAt <= Date.now()) return null;
  return cached.value;
}

/** Préremplit le cache (listes admins / intervenants / clients). */
export function primeActorAvatarRegistry(entries = []) {
  if (!Array.isArray(entries)) return;
  const expiresAt = Date.now() + ACTOR_CACHE_TTL_MS;
  entries.forEach((entry) => {
    if (entry?.id == null || entry.id === '') return;
    const role = normalizeActorRole(entry.role);
    const avatarUrl = String(entry.avatar_url || '').trim();
    if (!avatarUrl) return;
    const key = actorCacheKey(role, entry.id);
    const existing = actorCache.get(key);
    const displayName = entry.name || entry.nom || entry.nom_complet || existing?.value?.name || null;
    const merged = {
      ...(existing?.value || {}),
      id: entry.id,
      role: entry.role || existing?.value?.role,
      name: displayName,
      email: entry.email || entry.mail || existing?.value?.email || null,
      avatar_url: avatarUrl,
      code_couleur: entry.code_couleur || existing?.value?.code_couleur || null
    };
    actorCache.set(key, { value: merged, expiresAt });
    const nameKey = profileNameCacheKey(entry.role, displayName);
    if (nameKey) profileNameIndex.set(nameKey, avatarUrl);
  });
}

/** Résout avatar_url : URL directe → id (cache) → nom (index listes). */
export function resolveProfileAvatarUrl({ id, role, name, directUrl } = {}) {
  const direct = String(directUrl || '').trim();
  if (direct) return direct;

  const fromId = resolveActorAvatarUrl({ id, role });
  if (String(fromId || '').trim()) return fromId;

  const nameKey = profileNameCacheKey(role, name);
  if (nameKey && profileNameIndex.has(nameKey)) {
    return profileNameIndex.get(nameKey) || null;
  }

  const normalizedName = normalizeProfileNameKey(name);
  const roleNorm = normalizeActorRole(role);
  if (normalizedName && roleNorm) {
    const prefix = `${roleNorm}::`;
    for (const [key, url] of profileNameIndex.entries()) {
      if (!key.startsWith(prefix) || !url) continue;
      const cachedName = key.slice(prefix.length);
      if (cachedName === normalizedName || cachedName.startsWith(`${normalizedName} `)) {
        return url;
      }
    }
  }
  return null;
}

export function clearActorAvatarCaches() {
  actorCache.clear();
  actorInFlight.clear();
  profileNameIndex.clear();
}

export function resolveActorAvatarUrl({ id, role, snapshot, metadata } = {}) {
  const fromMeta = metadata?.actor_avatar_url;
  const fromSnap = snapshot?.avatar_url || snapshot?.avatar;
  const direct = fromMeta || fromSnap;
  if (String(direct || '').trim()) return direct;

  if (id != null && id !== '') {
    const cached = peekActorFromCache(role, id);
    if (cached?.avatar_url) return cached.avatar_url;
  }
  return null;
}

export function snapshotHasDisplayIdentity(snapshot) {
  if (!snapshot || snapshot.id == null || snapshot.id === '') return false;
  const label = String(snapshot.name || snapshot.nom_complet || snapshot.nom || snapshot.email || '').trim();
  return label.length > 0;
}

export const getActorEndpoints = (role, actorId, viewer = null) => {
  const normalizedRole = normalizeActorRole(role);
  const viewerRole = normalizeActorRole(viewer?.role);
  const viewerId = viewer?.id;
  const id = encodeURIComponent(String(actorId));
  const isSelf =
    viewerId != null
    && actorId != null
    && String(viewerId) === String(actorId)
    && viewerRole === normalizedRole;

  if (normalizedRole === 'superadmin' || normalizedRole === 'admin') {
    return [`/admins/${id}`, `/auth/superadmin/admins/${id}`];
  }
  if (normalizedRole === 'intervenant') {
    if (isSelf) return ['/intervenant/profil'];
    if (viewerRole === 'client') return [];
    return [`/intervenants/${id}`];
  }
  if (normalizedRole === 'client') {
    if (isSelf) return ['/client/profil'];
    if (viewerRole === 'intervenant') return [];
    return [`/clients/${id}`];
  }
  return [];
};

const extractActorPayload = (responseData) => {
  const data = responseData && typeof responseData === 'object' ? responseData : null;
  if (!data) return null;
  if (data.admin && typeof data.admin === 'object') return data.admin;
  if (data.intervenant && typeof data.intervenant === 'object') return data.intervenant;
  if (data.client && typeof data.client === 'object') return data.client;
  if (data.data && typeof data.data === 'object') {
    if (data.data.admin) return data.data.admin;
    if (data.data.intervenant) return data.data.intervenant;
    if (data.data.client) return data.data.client;
  }
  return data;
};

export const mergeActorSnapshot = (snapshot, payload, role) => ({
  ...snapshot,
  id: payload?.id ?? snapshot?.id,
  name: payload?.name || payload?.nom_complet || payload?.nom || snapshot?.name || null,
  email: payload?.email || snapshot?.email || null,
  role: payload?.role || snapshot?.role || snapshot?.actor_role || role,
  code_couleur: payload?.code_couleur || snapshot?.code_couleur || null,
  avatar: payload?.avatar || snapshot?.avatar || null,
  avatar_url: payload?.avatar_url || snapshot?.avatar_url || null,
  photo: payload?.photo || snapshot?.photo || null
});

export const getActorCurrent = async ({ actorSnapshot, actorId, actorRole, cacheKey, viewer = null }) => {
  const endpoints = getActorEndpoints(actorRole, actorId, viewer);
  if (endpoints.length === 0) return actorSnapshot;

  const inFlight = actorInFlight.get(cacheKey);
  if (inFlight) return inFlight;

  const requestPromise = (async () => {
    for (const endpoint of endpoints) {
      try {
        const response = await api.get(endpoint, {
          timeout: 3000,
          _skipOfflineQueue: true,
          _skipOfflineCache: true,
          validateStatus: (s) => (s >= 200 && s < 300) || s === 404
        });
        if (response.status === 404) {
          continue;
        }
        const payload = extractActorPayload(response?.data);
        if (!payload || typeof payload !== 'object') continue;

        const merged = mergeActorSnapshot(actorSnapshot, payload, actorRole);
        actorCache.set(cacheKey, {
          value: merged,
          expiresAt: Date.now() + ACTOR_CACHE_TTL_MS
        });
        return merged;
      } catch {
        // endpoint suivant
      }
    }
    actorCache.set(cacheKey, {
      value: actorSnapshot,
      expiresAt: Date.now() + ACTOR_MISSING_CACHE_TTL_MS
    });
    return actorSnapshot;
  })();

  actorInFlight.set(cacheKey, requestPromise);
  try {
    return await requestPromise;
  } finally {
    actorInFlight.delete(cacheKey);
  }
};
